/**
 * CustomerCommsPanel — Operations Console > Customer Communications.
 *
 * Platform-admin only.
 *  - Provider status banner (live vs. logged-only) so operators always know
 *    whether emails are actually being delivered.
 *  - Template list with category badges, enabled toggle, and per-template
 *    Preview + Test-send actions.
 *  - Delivery log with status filter chips.
 *
 * Talks to /api/comms/* — all admin-gated.
 */
import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import {
  Mail, MailCheck, MailWarning, MailX, Loader2, RefreshCw, Send,
  Eye, X, Power, AlertTriangle, FileText, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_COLORS = {
  marketing:  "#7dd3fc",
  invitation: "#fb923c",
  onboarding: "#fbbf24",
  trial:      "#c4b5fd",
  success:    "#86efac",
};

const STATUS_COLORS = {
  sent:             { bg: "rgba(34,197,94,0.10)",  border: "rgba(34,197,94,0.40)",  text: "#86efac", icon: MailCheck },
  failed:           { bg: "rgba(220,38,38,0.10)",  border: "rgba(220,38,38,0.40)",  text: "#fca5a5", icon: MailX },
  logged_not_sent:  { bg: "rgba(107,114,128,0.10)", border: "rgba(107,114,128,0.40)", text: "#cbd5e1", icon: Mail },
  pending:          { bg: "rgba(56,189,248,0.10)", border: "rgba(56,189,248,0.40)", text: "#7dd3fc", icon: MailWarning },
};

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return "—"; }
};

export default function CustomerCommsPanel() {
  const [provider, setProvider] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [log, setLog] = useState([]);
  const [counts, setCounts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [previewing, setPreviewing] = useState(null);
  const [testingTpl, setTestingTpl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t, l] = await Promise.all([
        api.get("/comms/provider"),
        api.get("/comms/templates"),
        api.get("/comms/log", { params: statusFilter ? { status: statusFilter } : {} }),
      ]);
      setProvider(p.data);
      setTemplates(t.data.templates || []);
      setLog(l.data.log || []);
      setCounts(l.data.counts || null);
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed to load comms data");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (code, enabled) => {
    try {
      await api.patch(`/comms/templates/${code}`, { enabled });
      toast.success(`Template ${enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed");
    }
  };

  const live = provider?.provider === "resend";

  return (
    <div
      className="rounded-xl border bg-[var(--fop-surface)]"
      style={{ borderColor: "var(--fop-border)" }}
      data-testid="customer-comms-panel"
    >
      <div className="px-5 py-4 border-b flex items-center justify-between gap-3 flex-wrap"
           style={{ borderColor: "var(--fop-border)" }}>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-md inline-flex items-center justify-center"
               style={{ background: "rgba(249, 115, 22, 0.10)", border: "1px solid rgba(249, 115, 22, 0.35)" }}>
            <Mail size={16} className="text-[var(--fop-primary)]" />
          </div>
          <div>
            <div className="text-[var(--fop-text)] font-semibold">Customer Communications</div>
            <div className="text-xs text-[var(--fop-text-3)] font-mono uppercase tracking-wider">
              Templates · delivery log · platform admin only
            </div>
          </div>
        </div>
        <button onClick={load} className="btn btn-ghost"
                style={{ minHeight: "36px", padding: "0 12px" }}
                data-testid="comms-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {provider && (
        <div className="px-5 py-3 border-b" style={{ borderColor: "var(--fop-border)" }}>
          <div
            className="rounded-md p-3 flex items-center gap-3 flex-wrap"
            style={{
              background: live ? "rgba(34,197,94,0.07)" : "rgba(245,158,11,0.07)",
              border: `1px solid ${live ? "rgba(34,197,94,0.45)" : "rgba(245,158,11,0.45)"}`,
            }}
            data-testid="comms-provider-banner"
          >
            {live ? (
              <CheckCircle2 size={16} className="text-[var(--fop-success)]" />
            ) : (
              <AlertTriangle size={16} style={{ color: "#fbbf24" }} />
            )}
            <div className="text-sm text-[var(--fop-text)]">
              <strong>{live ? "Live email delivery active" : "Console mode — emails logged only"}.</strong>{" "}
              <span className="text-[var(--fop-text-2)]">
                Provider: <span className="font-mono">{provider.provider}</span> ·
                From: <span className="font-mono">{provider.from}</span> ·
                Reply-To: <span className="font-mono">{provider.reply_to}</span>
                {!live && <> · Add <span className="font-mono">RESEND_API_KEY</span> to deployment env to flip to live.</>}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Templates */}
      <div className="px-5 py-4 border-b" style={{ borderColor: "var(--fop-border)" }}>
        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[var(--fop-text-3)] mb-3">
          Templates ({templates.length})
        </div>
        {loading ? (
          <div className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-2.5">
            {templates.map((t) => (
              <TemplateCard
                key={t.code}
                template={t}
                onPreview={() => setPreviewing(t)}
                onTestSend={() => setTestingTpl(t)}
                onToggle={(en) => toggleEnabled(t.code, en)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Log */}
      <div className="px-5 py-4 border-b flex flex-wrap gap-2 text-[11px] font-mono uppercase tracking-wider"
           style={{ borderColor: "var(--fop-border)" }}>
        <Chip label="All"  value={counts?.total ?? 0} active={!statusFilter} onClick={() => setStatusFilter("")} testid="log-filter-all" />
        {["sent", "failed", "logged_not_sent", "pending"].map((s) => (
          <Chip key={s} label={s.replace(/_/g, " ")} value={counts?.[s] ?? 0}
                active={statusFilter === s} onClick={() => setStatusFilter(statusFilter === s ? "" : s)}
                testid={`log-filter-${s}`}
                color={STATUS_COLORS[s].text} />
        ))}
      </div>

      <div data-testid="comms-log-list">
        {loading ? (
          <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
        ) : log.length === 0 ? (
          <div className="p-10 text-center text-[var(--fop-text-3)] text-sm" data-testid="comms-log-empty">
            No emails recorded {statusFilter ? `with status "${statusFilter}"` : "yet"}.
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: "var(--fop-border)" }}>
            {log.map((row) => <LogRow key={row.id} row={row} />)}
          </div>
        )}
      </div>

      {previewing && (
        <PreviewModal template={previewing} onClose={() => setPreviewing(null)} />
      )}
      {testingTpl && (
        <TestSendModal template={testingTpl} onClose={() => setTestingTpl(null)} onSent={load} />
      )}
    </div>
  );
}

function Chip({ label, value, active, onClick, testid, color }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-2.5 py-1 rounded transition-colors"
      style={{
        background: active ? "var(--fop-surface-3)" : "var(--fop-surface-2)",
        border: `1px solid ${active ? "var(--fop-primary)" : "var(--fop-border)"}`,
        color: color || "var(--fop-text-2)",
      }}
    >
      {label} · {value}
    </button>
  );
}

function TemplateCard({ template, onPreview, onTestSend, onToggle }) {
  const c = CATEGORY_COLORS[template.category] || "#cbd5e1";
  return (
    <div
      className="rounded-md p-3.5 flex flex-col gap-2"
      style={{ background: "var(--fop-surface-2)", border: "1px solid var(--fop-border)" }}
      data-testid={`template-card-${template.code}`}
    >
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0">
          <div className="font-semibold text-[var(--fop-text)] truncate text-sm">{template.label}</div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mt-0.5 truncate">
            {template.code}
          </div>
        </div>
        <span
          className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
          style={{ background: `${c}1a`, border: `1px solid ${c}66`, color: c }}
        >
          {template.category}
        </span>
      </div>
      {template.description && (
        <div className="text-xs text-[var(--fop-text-2)] leading-snug line-clamp-2">{template.description}</div>
      )}
      <div className="flex items-center gap-2 flex-wrap pt-1.5">
        <button onClick={onPreview} className="btn btn-ghost"
                style={{ minHeight: "32px", padding: "0 10px", fontSize: "12px" }}
                data-testid={`template-preview-${template.code}`}>
          <Eye size={12} /> Preview
        </button>
        <button onClick={onTestSend} className="btn btn-ghost"
                style={{ minHeight: "32px", padding: "0 10px", fontSize: "12px" }}
                data-testid={`template-test-${template.code}`}>
          <Send size={12} /> Test send
        </button>
        <button
          onClick={() => onToggle(!template.enabled)}
          className="btn btn-ghost"
          style={{
            minHeight: "32px", padding: "0 10px", fontSize: "12px",
            color: template.enabled ? "var(--fop-success)" : "var(--fop-text-3)",
          }}
          data-testid={`template-toggle-${template.code}`}
          title={template.enabled ? "Disable" : "Enable"}
        >
          <Power size={12} /> {template.enabled ? "Enabled" : "Disabled"}
        </button>
      </div>
    </div>
  );
}

function LogRow({ row }) {
  const s = STATUS_COLORS[row.status] || STATUS_COLORS.pending;
  const Icon = s.icon;
  return (
    <div className="px-5 py-3 flex items-start gap-3"
         data-testid={`comms-log-row-${row.id}`}>
      <Icon size={16} style={{ color: s.text }} className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap text-sm">
          <span className="text-[var(--fop-text)] truncate">{row.subject}</span>
          <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded"
                style={{ background: s.bg, border: `1px solid ${s.border}`, color: s.text }}>
            {row.status.replace(/_/g, " ")}
          </span>
        </div>
        <div className="text-xs text-[var(--fop-text-3)] truncate">
          {row.template_code} · → {Array.isArray(row.to) ? row.to.join(", ") : row.to}
          {row.error && <> · <span className="text-[var(--fop-danger)]">{row.error}</span></>}
        </div>
      </div>
      <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] shrink-0">
        {fmt(row.created_at)}
      </div>
    </div>
  );
}

function PreviewModal({ template, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/comms/templates/${template.code}/preview`, {});
        setData(data);
      } catch (e) {
        const m = e?.response?.data?.detail;
        toast.error(typeof m === "string" ? m : "Preview failed");
        onClose();
      }
    })();
  }, [template, onClose]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(2,6,23,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="preview-modal"
    >
      <div
        className="relative w-full max-w-3xl max-h-[92vh] overflow-y-auto rounded-xl"
        style={{
          background: "linear-gradient(180deg, #111827 0%, #0a0f1c 100%)",
          border: "1px solid var(--fop-border-2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose}
                className="absolute top-4 right-4 text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                data-testid="preview-modal-close">
          <X size={20} />
        </button>
        <div className="px-7 pt-7 pb-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--fop-primary)] mb-2">
            Preview · {template.code}
          </div>
          {!data ? (
            <div className="py-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-[var(--fop-text-3)]" /></div>
          ) : (
            <>
              <div className="mb-4">
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">Subject</div>
                <div className="text-[var(--fop-text)] font-medium">{data.subject}</div>
              </div>
              <div className="mb-4">
                <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-3)] mb-1">HTML preview</div>
                <iframe
                  title="email-preview"
                  srcDoc={data.html}
                  className="w-full rounded-md"
                  style={{ height: "480px", background: "#0a0f1c", border: "1px solid var(--fop-border-2)" }}
                  data-testid="preview-iframe"
                  // Security hardening (2026-09-08, exhaustive XSS sweep,
                  // P3): platform-admin-only preview, but the rendered
                  // HTML is templated from tenant-controlled variables
                  // (services/comms.py) -- sandbox strips script
                  // execution/top-level navigation/form-submit even if a
                  // template variable is ever left unescaped.
                  sandbox=""
                />
              </div>
              <details className="text-sm">
                <summary className="cursor-pointer text-[var(--fop-text-2)] text-xs font-mono uppercase tracking-wider">Variables · context</summary>
                <pre
                  className="mt-2 rounded p-3 overflow-x-auto"
                  style={{ background: "var(--fop-surface-2)", border: "1px solid var(--fop-border)" }}
                >{JSON.stringify(data.context, null, 2)}</pre>
              </details>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TestSendModal({ template, onClose, onSent }) {
  const [to, setTo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!to.trim()) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/comms/templates/${template.code}/test-send`, { to: to.trim() });
      setResult(data);
      toast.success(data.status === "sent" ? "Email sent" : `Logged (provider: ${data.provider})`);
      onSent?.();
    } catch (e) {
      const m = e?.response?.data?.detail;
      toast.error(typeof m === "string" ? m : "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(2,6,23,0.78)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      data-testid="test-send-modal"
    >
      <div
        className="relative w-full max-w-md rounded-xl"
        style={{
          background: "linear-gradient(180deg, #111827 0%, #0a0f1c 100%)",
          border: "1px solid var(--fop-border-2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button onClick={onClose}
                className="absolute top-4 right-4 text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"
                data-testid="test-send-close">
          <X size={20} />
        </button>
        <div className="px-7 pt-7 pb-6">
          <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[var(--fop-primary)] mb-2">
            Test send · {template.code}
          </div>
          {result ? (
            <div className="text-center py-2">
              <CheckCircle2 size={32} className="text-[var(--fop-success)] mx-auto mb-3" />
              <div className="font-display text-lg text-[var(--fop-text)] mb-1">
                {result.status === "sent" ? "Delivered" : "Logged"}
              </div>
              <div className="text-sm text-[var(--fop-text-2)] mb-4">
                Provider: <span className="font-mono">{result.provider}</span>
                {result.provider_id && <> · ID <span className="font-mono">{result.provider_id}</span></>}
              </div>
              <button onClick={onClose} className="btn btn-primary"
                      style={{ minHeight: "40px", padding: "0 20px" }}
                      data-testid="test-send-done">
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h3 className="font-display text-xl text-[var(--fop-text)]">Send a test email</h3>
              <p className="text-sm text-[var(--fop-text-2)]">
                Uses the template's example context (sample names, dates, etc.) so you can
                inspect rendered output without touching real customer data.
              </p>
              <label className="block">
                <div className="text-xs font-mono uppercase tracking-wider text-[var(--fop-text-2)] mb-1.5">Recipient</div>
                <input type="email" required value={to}
                       onChange={(e) => setTo(e.target.value)}
                       placeholder="you@forgedopshq.com"
                       data-testid="test-send-to" />
              </label>
              <button type="submit" disabled={submitting || !to.trim()}
                      className="btn btn-primary w-full"
                      style={{ minHeight: "44px" }}
                      data-testid="test-send-submit">
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <><Send size={14} /> Send test</>}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
